import { InfoIcon } from '@phosphor-icons/react';
import { Button, Popover } from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import { POStatus } from '@utility/constants/common';
import { DateFormat, DateUtility } from '@utility/date';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { flagImages } from '../../legacy-bridge';
import type { InventoryItem, ScmPlace, ScmShipment } from '../../lib/inventory';

const GRID =
  'sk:grid sk:grid-cols-[minmax(0,6fr)_2fr_4fr] sk:items-center sk:gap-x-3';

const count = (value?: number) => CommonUtility.numberWithCommas(value || 0);

const sum = (shipments: ScmShipment[]) =>
  shipments.reduce((total, x) => total + (x.qty || 0), 0);

const placeId = (place?: string | ScmPlace) =>
  typeof place === 'string' ? place : place?._id;

function Place({ place }: { place?: string | ScmPlace }) {
  if (!place || typeof place === 'string') return null;

  const flag =
    flagImages[
      (place.marketplace_region_name || place.address || '').toUpperCase()
    ];

  return (
    <span className="sk:flex sk:min-w-0 sk:items-center sk:gap-1.5">
      {flag ? (
        <img
          src={flag}
          alt=""
          className="sk:h-2.5 sk:w-3.5 sk:shrink-0 sk:rounded-xs sk:object-cover"
        />
      ) : null}
      <span className="sk:truncate">{place.name}</span>
    </span>
  );
}

function ShipmentLine({
  to,
  place,
  shipment,
  completion,
}: {
  to: string;
  place?: string | ScmPlace;
  shipment: ScmShipment;
  completion: ReactNode;
}) {
  return (
    <div className={GRID}>
      <Link
        to={to}
        className="sk:flex sk:min-w-0 sk:flex-col sk:text-fg sk:no-underline sk:hover:underline"
      >
        <Place place={place} />
        <span className="sk:text-xs sk:text-fg-tertiary">
          {shipment.display_id || shipment.po_display_id}
        </span>
      </Link>
      <span className="sk:tabular-nums">{count(shipment.qty)}</span>
      {completion}
    </div>
  );
}

function Total({ label, value }: { label: string; value: number }) {
  return (
    <div className={`${GRID} sk:border-t sk:border-rule sk:pt-2`}>
      <span className="sk:text-fg-secondary">{label}</span>
      <span className="sk:font-medium sk:tabular-nums">{count(value)}</span>
    </div>
  );
}

function Heading({ children }: { children: string }) {
  return (
    <div className={`${GRID} sk:text-xs sk:text-fg-tertiary`}>
      <span>{children}</span>
      <span>Units</span>
      <span>Completion Date</span>
    </div>
  );
}

/**
 * Units on their way in (or, `inbound` off, being prepared to leave), with the
 * shipments and predicted shipments behind the figure.
 */
export function ShipmentsBreakdown({
  item,
  providerId,
  inbound,
}: {
  item: InventoryItem;
  providerId: string;
  inbound: boolean;
}) {
  const shipments = (item.scm?.existing_shipments ?? []).filter(x =>
    inbound
      ? placeId(x.destination) === providerId
      : x.origin?._id === providerId && x.status === 'preparing',
  );
  const predicted = (item.scm?.predicted_shipments ?? []).filter(
    x => placeId(x.destination) === providerId,
  );

  if (!shipments.length && !predicted.length) {
    return <span className="sk:tabular-nums">0</span>;
  }

  const existingUnits = sum(shipments);
  const predictedUnits = sum(predicted);

  return (
    <Popover
      aria-label={inbound ? 'Inbound shipments' : 'Outbound shipments'}
      placement="bottom"
      className="sk:w-110 sk:max-w-[90vw] sk:p-3"
      trigger={
        <Button variant="ghost" size="sm" className="sk:-ml-3 sk:tabular-nums">
          {count(existingUnits)}
          <InfoIcon size={14} aria-hidden className="sk:text-fg-tertiary" />
        </Button>
      }
    >
      <div className="sk:flex sk:max-h-[50vh] sk:flex-col sk:gap-2 sk:overflow-y-auto sk:text-dense">
        {shipments.length > 0 ? (
          <>
            <Heading>{`Shipments ${inbound ? 'From' : 'To'}`}</Heading>
            {shipments.map(shipment => {
              const isOverdue =
                shipment.status !== POStatus.completed &&
                DateUtility.isDateAfter(
                  new Date(),
                  new Date(shipment.ETA ?? ''),
                );
              const date = DateUtility.formatDate(
                shipment.ETA,
                DateFormat.date,
              );

              return (
                <ShipmentLine
                  key={shipment._id}
                  to={`/app/scm/shipments/${shipment._id}`}
                  place={inbound ? shipment.origin : shipment.destination}
                  shipment={shipment}
                  completion={
                    isOverdue ? (
                      <span className="sk:text-danger">Overdue ({date})</span>
                    ) : (
                      <span>{date}</span>
                    )
                  }
                />
              );
            })}
            <Total label="Total (Existing Shipments)" value={existingUnits} />
          </>
        ) : null}
        {predicted.length > 0 ? (
          <>
            <Heading>Predicted Shipments From</Heading>
            {predicted.map((shipment, index) => {
              const originId = shipment.origin_id || shipment.origin?._id;
              const query = new URLSearchParams({
                origin_id: originId ?? '',
                destination: placeId(shipment.destination) ?? '',
                type: shipment.origin_type ?? '',
                step: '1',
              });

              return (
                <ShipmentLine
                  key={`${originId}-${index}`}
                  to={`/app/scm/shipments/create/?${query}`}
                  place={shipment.origin}
                  shipment={shipment}
                  completion={
                    <span>
                      {DateUtility.formatDate(shipment.ETA, DateFormat.date)}
                    </span>
                  }
                />
              );
            })}
            <Total label="Total (Predicted Shipments)" value={predictedUnits} />
          </>
        ) : null}
        {shipments.length > 0 && predicted.length > 0 ? (
          <Total label="Total" value={existingUnits + predictedUnits} />
        ) : null}
      </div>
    </Popover>
  );
}
